import { KnBrDraftOrdersService } from 'src/app/store/kn-br-draft-orders/kn-br-draft-orders.service';
import { KnBrAddRequestEntry } from './../../models/kn-br-order.model';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Product } from '@spartacus/core';
import { KnBrProduct } from '../../models/kn-br-products.model';
import { CurrentProductService } from '@spartacus/storefront';
import { ActiveCartService, CartAdapter } from '@spartacus/cart/base/core';
import { MultiCartFacade } from '@spartacus/cart/base/root';
import { Observable, Subject } from 'rxjs';
import { filter, finalize, takeUntil } from 'rxjs/operators';
import { KnBrCommonService } from '../../services/kn-br-common.service';
import { KnBrCartContextService } from '../../store/kn-br-cart-context/kn-br-cart-context.service';
import { KnBrQuoteContextService } from '../../store/kn-br-quote-context/kn-br-quote-context.service';
import { KnBrSelectCartComponent } from './kn-br-select-cart/kn-br-select-cart.component';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { KnBrNumbersOnlyDirective } from '../../shared/directive/kn-br-numbers-only.directive';
import { AsyncPipe, NgClass } from '@angular/common';
import { ProductSwitchService } from 'src/app/services/kn-br-product-switch.service';
import { KnBrAddToCartAlertPopupComponent } from './kn-br-add-to-cart-alert-popup.component';
import { Router } from '@angular/router';
import { KnBrDraftOrdersAdapter } from 'src/app/store/kn-br-draft-orders/kn-br-draft-orders.adapter';
import { KN_BR_CUSTOMER_CURRENT } from 'src/constants/api.endpoints.constant';
import { CustomerData } from 'src/app/models/customer.model';
import { KnBrCustomerContextService } from 'src/app/store/kn-br-customer-context/kn-br-customer-context.service';

const DEFAULT_ERROR_MESSAGE = 'An unexpected error occurred';

@Component({
  selector: 'kn-br-add-to-cart',
  templateUrl: './kn-br-add-to-cart.component.html',
  styleUrls: ['./kn-br-add-to-cart.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [ReactiveFormsModule, NgClass, KnBrNumbersOnlyDirective, AsyncPipe]
})
export class KnBrAddToCartComponent implements OnInit, OnDestroy {
  @Input() type = 'PDP';
  @Input() productCode: string;
  /**
   * As long as we do not support #5026, we require product input, as we need
   *  a reference to the product model to fetch the stock data.
   */
  @Input() product: KnBrProduct;
  @Input() productList: KnBrAddRequestEntry[];
  @Input() plpQuantity: number | string;

  minQuantity: number;
  modalRef: NgbModalRef;
  hasStock = false;
  quantity = 1;
  cartContext: string;
  quoteContext: string;
  cartId: string;
  customerNumber: string = KN_BR_CUSTOMER_CURRENT;
  PDPProductData: any;
  isCreateCartInitiated = false;

  addToCartForm = new UntypedFormGroup({
    quantity: new UntypedFormControl(1),
  });
  isSaveDisabled$: Observable<boolean> = this.activeCartService.getLoading();
  customerContextData$: Observable<CustomerData> = this.knBrCustomerContextService.get$;

  private destroy$ = new Subject<void>();

  constructor(
    protected modalService: NgbModal,
    protected currentProductService: CurrentProductService,
    protected cd: ChangeDetectorRef,
    protected activeCartService: ActiveCartService,
    protected knBrCartContextService: KnBrCartContextService,
    protected knBrQuoteContextService: KnBrQuoteContextService,
    protected commonService: KnBrCommonService,
    protected knBrDraftOrderService: KnBrDraftOrdersService,
    private multiCartFacade: MultiCartFacade,
    private productSwitchService: ProductSwitchService,
    private router: Router,
    private cartAdapter: CartAdapter,
    private knBrDraftOrdersAdapter: KnBrDraftOrdersAdapter,
    protected knBrCustomerContextService: KnBrCustomerContextService
  ) {}

  private get qtyCtrl(): UntypedFormControl {
    return this.addToCartForm.get('quantity') as UntypedFormControl;
  }

  ngOnInit() {
    this.customerContextData$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.customerNumber = value?.customerNumber || KN_BR_CUSTOMER_CURRENT;
    });

    // Fallback for when a cart is active in Spartacus but no cart context is stored yet.
    this.activeCartService
      .getActive()
      .pipe(filter(Boolean), takeUntil(this.destroy$))
      .subscribe((activeCartId) => {
        if (!this.cartContext) {
          this.cartId = activeCartId?.code;
        }
      });

    this.knBrCartContextService.get$.pipe(takeUntil(this.destroy$)).subscribe((cartCode) => {
      this.cartContext = cartCode;
      if (cartCode) {
        this.cartId = cartCode;
        // A cart was just created by addToCart(); continue the add flow.
        if (this.isCreateCartInitiated) {
          this.isCreateCartInitiated = false;
          this.checkAddToCartCompatibilityAndProceed(cartCode);
        }
      }
      this.cd.markForCheck();
    });

    this.knBrQuoteContextService.get$.pipe(takeUntil(this.destroy$)).subscribe((quoteCode) => {
      this.quoteContext = quoteCode;
      this.cd.markForCheck();
    });

    this.productSwitchService.productVariantCode$.pipe(takeUntil(this.destroy$)).subscribe((variant) => {
      if (variant) {
        this.PDPProductData = variant;
        this.productCode = variant.code;
        this.cd.markForCheck();
      }
    });

    this.initProduct();
  }

  private initProduct(): void {
    if (this.product) {
      this.productCode = this.product.code;
      this.setStockInfo(this.product);
      return;
    }

    if (this.productCode) {
      // force hasStock and quantity, as we do not have more info:
      this.quantity = 1;
      this.hasStock = true;
      return;
    }

    this.currentProductService
      .getProduct()
      .pipe(filter(Boolean), takeUntil(this.destroy$))
      .subscribe((product: Product) => {
        this.productCode = product.code;
        this.PDPProductData = product;
        this.setStockInfo(product);
        this.cd.markForCheck();
      });

    this.qtyCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((quantity) => {
      this.commonService.setPDPQuantity(quantity);
    });
  }

  increaseQuantity(): void {
    this.qtyCtrl.setValue((this.qtyCtrl.value ?? 1) + 1);
  }

  decreaseQuantity(): void {
    const current = this.qtyCtrl.value ?? 1;
    if (current > 1) {
      this.qtyCtrl.setValue(current - 1);
    }
  }

  onQuantityBlur(): void {
    let qty = Number(this.qtyCtrl.value);
    if (isNaN(qty) || qty < 1) {
      qty = 1;
    }
    const qtyBasedOnMoq = this.commonService.getQuantityBasedOnMoq(qty, this.minQuantity);
    this.qtyCtrl.patchValue(qtyBasedOnMoq);
    this.quantity = qtyBasedOnMoq;
  }

  setStockInfo(product: any): void {
    this.quantity = product.minOrderQuantity || 1;
    this.minQuantity = this.quantity;
    this.hasStock = product.stock && product.stock.stockLevelStatus !== 'outOfStock';
    this.qtyCtrl.setValidators([Validators.min(this.minQuantity)]);
    this.qtyCtrl.updateValueAndValidity();
    this.qtyCtrl.patchValue(this.quantity);
  }

  showErrMsg(
    header: string,
    message: string,
    showViewCart: boolean,
    showClearCart: boolean,
    overlimitExceeded: boolean,
    current_cartId: string
  ) {
    const modalRef = this.modalService.open(KnBrAddToCartAlertPopupComponent, {
      centered: true,
      size: 'md',
      backdrop: 'static',
      keyboard: false,
      windowClass: 'kn-br-add-to-cart-alert-modal',
      backdropClass: 'kn-br-add-to-cart-alert-backdrop',
    });
    const instance = modalRef.componentInstance;
    instance.header = header;
    instance.message = message;
    instance.showViewCart = showViewCart;
    instance.showClearCart = showClearCart;
    instance.viewCartText = 'View Cart';
    instance.clearCartText = 'Clear Cart';
    instance.buttonText = 'OK';
    modalRef.result
      .then((result) => {
        switch (result?.action) {
          case 'viewCart':
            this.router.navigateByUrl('/salesCart');
            break;
          case 'clearCart':
            this.clearCart();
            break;
          case 'ok':
            if (overlimitExceeded) {
              this.getCartEntries(current_cartId);
            }
            break;
        }
      })
      .catch(() => {});
  }

  clearCart(): void {
    const cartId = this.cartContext || this.cartId;
    if (!cartId) {
      this.resetCart();
      return;
    }

    this.commonService.showSpinner$.next(true);
    this.cartAdapter
      .delete('current', cartId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.commonService.showSpinner$.next(false))
      )
      .subscribe({
        next: () => this.resetCart(),
        error: (error) => {
          console.error(error);
          this.resetCart();
        },
      });
  }

  private resetCart(): void {
    this.knBrCartContextService.reset();
    this.cartId = null;
  }

  addToCart(): void {
    this.commonService.showSpinner$.next(true);

    const cartId = this.cartContext || this.cartId;
    if (cartId) {
      this.checkAddToCartCompatibilityAndProceed(cartId);
      return;
    }

    // The cart-context subscription continues the flow once the new cart is set.
    this.isCreateCartInitiated = true;
    this.knBrDraftOrderService.create({ customerNumber: this.customerNumber });
  }

  private getEntryRequest() {
    const isPlp = this.type === 'PLP';
    const source = isPlp ? this.product : this.PDPProductData;
    return {
      code: isPlp ? source?.defaultVariantProduct : source?.defaultVariantProduct || source?.code,
      quantity: isPlp ? this.plpQuantity : this.qtyCtrl.value || this.quantity || 1,
      salesArea: source?.salesOrg,
    };
  }

  checkAddToCartCompatibilityAndProceed(current_cartId: string) {
    const { code, quantity, salesArea } = this.getEntryRequest();

    this.knBrDraftOrdersAdapter
      .checkAddToCartCompatibility({ productCode: code, salesOrg: salesArea, quantity, cartId: current_cartId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const result = (response as any)?.body ?? response ?? {};
          const isCompatible = String(result.compatible) === 'true';
          const overlimitExceeded = String(result.overlimitExceeded) === 'true';

          if (overlimitExceeded) {
            this.commonService.showSpinner$.next(false);
            this.showErrMsg(
              'Quantity Overage Alert',
              'Requested quantity is higher than maximum allowed. Please add reason at checkout.',
              false,
              false,
              true,
              current_cartId
            );
          } else if (isCompatible) {
            this.getCartEntries(current_cartId);
          } else {
            this.commonService.showSpinner$.next(false);
            this.showErrMsg(
              'Add to Cart Compatibility Error',
              result.message || DEFAULT_ERROR_MESSAGE,
              true,
              true,
              false,
              current_cartId
            );
          }
        },
        error: (error) => {
          this.commonService.showSpinner$.next(false);
          const message = error?.error?.errors?.[0]?.message || error?.error?.message || DEFAULT_ERROR_MESSAGE;
          this.showErrMsg('Add to Cart Compatibility Error', message, true, false, false, '');
        },
      });
  }

  getCartEntries(current_cartId: string) {
    this.commonService.showSpinner$.next(true);

    this.knBrDraftOrdersAdapter
      .addEntry({ ...this.getEntryRequest(), cartId: current_cartId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.commonService.showSpinner$.next(false);
          this.multiCartFacade.reloadCart(current_cartId);
          this.commonService.setMiniCartOpen(true);
          this.cd.markForCheck();
        },
        error: (error) => {
          this.commonService.showSpinner$.next(false);
          const message = error?.error?.errors?.[0]?.message || DEFAULT_ERROR_MESSAGE;
          this.showErrMsg('Cannot add item to cart', message, true, true, false, '');
        },
      });
  }

  openModal() {
    this.quantity = this.qtyCtrl.value;
    this.modalRef = this.modalService.open(KnBrSelectCartComponent, {
      centered: true,
      size: 'lg',
    });
    const modalInstance = this.modalRef.componentInstance;
    modalInstance.productCode = this.productCode;
    modalInstance.quantity = this.quantity;
    modalInstance.productList = this.productList;
  }

  addProductsToCart() {
    if (!this.cartContext) {
      this.openModal();
    } else if (this.productList && this.productList.length > 0) {
      this.knBrDraftOrderService.quickEntry({
        cartId: this.cartContext,
        request: this.productList,
      });
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
